import { useFeatureFlag } from '@src/hooks';
import {
  Box,
  ButtonVariants,
  Dialog,
  DialogContent,
  FormGroup,
  HStack,
  IconName,
  Input,
  NotificationVariants,
  Post,
  ProductTypeEnum,
  Text,
  useDisclosure,
  useDynamicCallback,
  useEndpointsContext,
  useGlobalToasts,
  useSecuritiesContext,
  VStack,
  type IPositionCloseRequest,
  type Security,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

type PositionCloseRequest = Pick<
  IPositionCloseRequest,
  'RequestID' | 'Asset' | 'ClosingPrice' | 'MarketAccount' | 'PriceType'
>;

interface PositionCloseResponseData {
  Timestamp: string;
  RequestID: string;
  Asset: string;
  Status: string;
  RealizedPnL: string;
  RealizedPnLCurrency: string;
  ClosedPosition: string;
}

interface PositionCloseResponse {
  type: string;
  ts: string;
  data: PositionCloseResponseData[];
}

/** The row data shape expected by this hook — works for both CustomerPosition and CustomerMargin rows. */
interface ExpireOptionRow {
  Symbol: string;
  MarketAccount: string;
  Counterparty?: string;
  Amount?: string;
}

interface ExpireOptionState {
  row: ExpireOptionRow;
  security: Security;
}

interface UseExpireOptionDialogProps {
  /** Called after a successful expire to refresh the table data */
  onSuccess?: () => void;
}

export function useExpireOptionDialog({ onSuccess }: UseExpireOptionDialogProps = {}) {
  const { orgApiEndpoint } = useEndpointsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { allowCustomerOptionManualExpiry } = useFeatureFlag();
  const { add: addToast } = useGlobalToasts();
  const disclosure = useDisclosure();

  const [state, setState] = useState<ExpireOptionState | undefined>();
  const [settlementRate, setSettlementRate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const postToOrgApi = useCallback(
    <TResponse,>(path: string, payload: object) =>
      Post<TResponse>(orgApiEndpoint!, path, payload, { headers: { 'Content-Type': 'application/json' } }),
    [orgApiEndpoint]
  );

  const openDialog = useCallback(
    (row: ExpireOptionRow, security: Security) => {
      setState({ row, security });
      setSettlementRate('');
      setIsSubmitting(false);
      disclosure.open();
    },
    [disclosure]
  );

  const handleExpire = useDynamicCallback(async () => {
    if (!orgApiEndpoint || !state || !settlementRate) {
      return;
    }

    setIsSubmitting(true);

    const payload: PositionCloseRequest = {
      RequestID: uuid(),
      Asset: state.row.Symbol,
      ClosingPrice: settlementRate,
      MarketAccount: state.row.MarketAccount,
      PriceType: 'Underlying',
    };

    try {
      const response = await postToOrgApi<PositionCloseResponse>('/customer/positions/close', payload);

      const result = response.data?.[0];
      if (result?.Status === 'Success') {
        addToast({
          text: `Option expired. Realized PnL: ${result.RealizedPnL} ${result.RealizedPnLCurrency}`,
          variant: NotificationVariants.Positive,
        });
        disclosure.close();
        onSuccess?.();
      } else {
        addToast({
          text: `Failed to expire option: ${result?.Status ?? 'Unexpected response'}`,
          variant: NotificationVariants.Negative,
        });
        disclosure.close();
      }
    } catch (e: any) {
      addToast({
        text: e?.message ?? 'Failed to expire option',
        variant: NotificationVariants.Negative,
      });
      disclosure.close();
    } finally {
      setIsSubmitting(false);
    }
  });

  const getExpireOptionMenuItem = useDynamicCallback((params: GetContextMenuItemsParams): MenuItemDef | undefined => {
    if (!allowCustomerOptionManualExpiry) {
      return undefined;
    }

    // EntityAdmin wraps rows in EntityAdminClass — unwrap via .data if present
    const data: ExpireOptionRow | undefined = (params.node?.data as any)?.data ?? params.node?.data;
    if (!data?.Symbol) {
      return undefined;
    }

    const security = securitiesBySymbol.get(data.Symbol);
    if (!security || security.ProductType !== ProductTypeEnum.Option) {
      return undefined;
    }

    return {
      name: 'Expire Option',
      icon: `<i class="ag-icon ${IconName.ClockEnd}"/>`,
      action: () => openDialog(data, security),
    };
  });

  const settleValueLabel =
    state?.security.SettleValueType === 'Inverted' ? 'Inverse' : (state?.security.SettleValueType ?? '-');

  const expireOptionDialog: ReactNode = useMemo(
    () => (
      <Dialog
        key="expire-option-dialog"
        {...disclosure}
        title="Expire Option"
        showClose
        confirmLabel="Expire"
        variant={ButtonVariants.Negative}
        confirmDisabled={!settlementRate || isSubmitting}
        confirmLoading={isSubmitting}
        stretchButtons
        onConfirm={handleExpire}
        closeOnConfirm={false}
        onCloseComplete={() => {
          setState(undefined);
        }}
        width={420}
      >
        <DialogContent align="left" overflow="auto">
          <VStack gap="spacing16" w="100%">
            <Box p="spacing12" borderRadius="borderRadiusDefault" background="backgroundCard" w="100%">
              <VStack gap="spacing8" w="100%">
                <SummaryRow label="Customer" value={state?.row.Counterparty} />
                <SummaryRow label="Market Account" value={state?.row.MarketAccount} />
                <SummaryRow label="Asset ID" value={state?.row.Symbol} />
                <SummaryRow label="Position Quantity" value={state?.row.Amount} />
                <SummaryRow label="Option Type" value={state?.security.OptionType} />
                <SummaryRow label="Underlying" value={state?.security.UnderlyingSymbol} />
                <SummaryRow label="Settlement Type" value={settleValueLabel} />
                <SummaryRow label="Strike Price" value={state?.security.StrikePrice} />
                <SummaryRow label="Price Type" value="Underlying" />
              </VStack>
            </Box>

            <FormGroup label="Settlement Rate (Underlying Price)" w="100%">
              <Input
                data-testid="expire-option-settlement-rate"
                value={settlementRate}
                onChange={e => setSettlementRate(e.target.value)}
                placeholder="e.g. 66020"
                autoFocus
              />
            </FormGroup>
          </VStack>
        </DialogContent>
      </Dialog>
    ),
    [disclosure, state, settlementRate, isSubmitting, handleExpire, settleValueLabel]
  );

  return { getExpireOptionMenuItem, expireOptionDialog };
}

function SummaryRow({ label, value }: { label: string; value?: string }) {
  return (
    <HStack justifyContent="space-between" w="100%">
      <Text color="colorTextSubtle" fontSize="fontSizeSmall">
        {label}
      </Text>
      <Text fontSize="fontSizeSmall">{value ?? '-'}</Text>
    </HStack>
  );
}
